import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

const getDefaultFiles2 = (
  defaultValues?: DefaultDropzoneValues
): (IFile | string)[] => {
  if (!defaultValues) return [];
  return Array.isArray(defaultValues) ? [...defaultValues] : [defaultValues];
};

export const ImageDropzone = ({
  onDrop,
  defaultValues,
  type,
  multiple,
  preview,
  onFileToLarge,
}: ImageDropzoneProps) => {
  const [files, setFiles] = useState<(IFile | string)[]>(
    getDefaultFiles2(defaultValues)
  );
  // const { showWarning } = useToaster();
  const types = Array.isArray(type) ? type : [type];
  const [accept] = useState(() => {
    let defaultVal = {};
    for (const type of types) {
      defaultVal = { ...defaultVal, [acceptTypes[type]]: [] };
    }
    return defaultVal;
  });

  useEffect(() => {
    setFiles(getDefaultFiles2(defaultValues));
  }, [defaultValues]);

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple,
    maxSize: 100 * 1024 * 1024,
    onDropRejected: (rejectedFiles) => {
      if (
        rejectedFiles.some((x) =>
          x.errors.some((e) => e.code === "file-too-large")
        )
      ) {
        if (onFileToLarge) onFileToLarge();
      }
    },
    onDrop: (acceptedFiles) => {
      const droppedFiles = acceptedFiles.map((x) => {
        const acceptedFile = acceptedFiles[0];
        const fileToSet = Object.assign(acceptedFile, {
          preview: URL.createObjectURL(acceptedFile),
        } as IFile);
        return fileToSet;
      });
      if (acceptedFiles.length < 1) return;
      setFiles(droppedFiles);
      onDrop(droppedFiles);
    },
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      for (const f of files ?? []) {
        if (typeof f === "string") URL.revokeObjectURL(f);
        else URL.revokeObjectURL(f.preview);
      }
    };
  }, []);

  const anyFile = files && files.length > 0;

  const acceptedFormats = types.map((x) => acceptFormats[x]).flat();

  return (
    <div
      {...getRootProps({
        className:
          "rounded bg-slate-100 dark:bg-slate-700/30 border border-dashed border-slate-300 dark:border-slate-700 text-center p-2 cursor-pointer h-full",
      })}
      data-testid={`image-dropzone-area`}
    >
      <input {...getInputProps()} />
      {!anyFile ? (
        <>
          <svg
            className="inline-flex w-4 h-4 fill-slate-400 dark:fill-slate-500 mb-3"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 4c-.3 0-.5.1-.7.3L1.6 10 3 11.4l4-4V16h2V7.4l4 4 1.4-1.4-5.7-5.7C8.5 4.1 8.3 4 8 4ZM1 2h14V0H1v2Z" />
          </svg>
          <label
            htmlFor="upload"
            className="block text-sm text-slate-500 dark:text-slate-400 italic"
          >
            Akceptujemy pliki w formacie {acceptedFormats.join(", ")}
          </label>
        </>
      ) : (
        <DropzonePreview
          files={files}
          type={type}
          multiple={multiple}
          preview={preview}
        />
      )}
    </div>
  );
};

interface DropzonePreviewProps {
  files: (string | IFile)[];
  type: DropzoneType | DropzoneType[];
  multiple?: boolean;
  preview?: "image" | "file";
}
const DropzonePreview = ({
  files,
  multiple,
  type,
  preview = type == "image" ? "image" : "file",
}: DropzonePreviewProps) => {
  if (multiple) throw new Error("Not implemented");

  if (preview === "image") {
    const src = typeof files[0] === "string" ? files[0] : files[0].preview;
    return (
      <img
        src={src}
        style={img}
        // Revoke data uri after image
        onLoad={() => {
          URL.revokeObjectURL(src);
        }}
      />
    );
  }
  const fileNames = files.map((x) => {
    if (typeof x === "string") return x;
    return x.name;
  });

  return (
    <div>
      {fileNames.map((fileName) => (
        <div key={fileName}>{fileName}</div>
      ))}
    </div>
  );
};

const img: React.CSSProperties = {
  display: "block",
  width: "auto",
  maxWidth: "100%",
  maxHeight: "200px",
  objectFit: "contain" as const,
  margin: "0 auto",
};

const acceptFormats: { [key in DropzoneType]: string[] } = {
  image: ["PNG", "JPEG", "WEBP"],
  pdf: ["PDF"],
};
const acceptTypes = {
  image: "image/*",
  pdf: "application/pdf",
};

type DefaultDropzoneValues = string | IFile | string[] | IFile[];
interface ImageDropzoneProps {
  onDrop: (file: IFile[]) => void;
  defaultValues?: DefaultDropzoneValues;
  type: DropzoneType | DropzoneType[];
  multiple?: boolean;
  preview?: "image" | "file";
  onFileToLarge?: () => void;
}

export type DropzoneType = keyof typeof acceptTypes;
export interface IFile extends File {
  preview: string;
}
